import PropTypes from "prop-types";
import Card from "@mui/material/Card";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";
import DefaultNavbar from "sections/DefaultNavbar";
import DefaultFooter from "sections/DefaultFooter";
import routes from "routes";
import footerRoutes from "footer.routes";
import bgImage from "assets/images/bg-presentation.webp";

// Every public page used to repeat the same navbar block, the same hero
// MKBox with its own gradient maths, and the same footer -- nine copies that
// had already drifted apart (ContactUs pinned its navbar, the rest did not).
// One layout keeps the chrome identical and leaves pages holding only content.
function PublicLayout({ title, subtitle, eyebrow, size, sheet, children }) {
  const heroHeight = { sm: "40vh", md: "55vh", lg: "70vh" }[size];

  return (
    <>
      <DefaultNavbar
        routes={routes}
        action={{ type: "internal", route: "/login", label: "LOGIN", color: "info" }}
        sticky
      />
      <MKBox
        minHeight={heroHeight}
        width="100%"
        sx={{
          backgroundImage: ({ functions: { linearGradient, rgba }, palette: { brand } }) =>
            `${linearGradient(
              rgba(brand.navyDark, 0.82),
              rgba(brand.navy, 0.72)
            )}, url(${bgImage})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          display: "grid",
          placeItems: "center",
        }}
      >
        <Container>
          <Grid
            container
            item
            xs={12}
            lg={9}
            flexDirection="column"
            alignItems="center"
            sx={{ mx: "auto", textAlign: "center" }}
          >
            {eyebrow && (
              <MKTypography
                variant="button"
                color="white"
                fontWeight="bold"
                textTransform="uppercase"
                opacity={0.85}
                mb={1}
                sx={{ letterSpacing: "0.12em" }}
              >
                {eyebrow}
              </MKTypography>
            )}
            <MKTypography
              variant="h1"
              color="white"
              sx={({ breakpoints, typography: { size: fontSize } }) => ({
                [breakpoints.down("md")]: { fontSize: fontSize["2xl"] },
              })}
            >
              {title}
            </MKTypography>
            {subtitle && (
              <MKTypography
                variant="body1"
                color="white"
                opacity={0.9}
                mt={1.5}
                px={{ xs: 1, md: 6 }}
              >
                {subtitle}
              </MKTypography>
            )}
          </Grid>
        </Container>
      </MKBox>

      {sheet ? (
        <Card
          sx={{
            p: { xs: 2, md: 3 },
            mx: { xs: 2, lg: 3 },
            mt: -8,
            mb: 4,
            boxShadow: ({ boxShadows: { xxl } }) => xxl,
          }}
        >
          {children}
        </Card>
      ) : (
        <MKBox mt={-8} mb={4} position="relative" zIndex={2}>
          {children}
        </MKBox>
      )}

      <MKBox pt={6} px={1} mt={6}>
        <DefaultFooter content={footerRoutes} />
      </MKBox>
    </>
  );
}

PublicLayout.defaultProps = {
  subtitle: "",
  eyebrow: "",
  size: "sm",
  sheet: true,
};

PublicLayout.propTypes = {
  title: PropTypes.node.isRequired,
  subtitle: PropTypes.node,
  eyebrow: PropTypes.string,
  size: PropTypes.oneOf(["sm", "md", "lg"]),
  // `sheet` draws the familiar floating white card. Pages that compose their
  // own full-bleed sections (the home page) turn it off.
  sheet: PropTypes.bool,
  children: PropTypes.node.isRequired,
};

export default PublicLayout;
